import * as React from 'react';
import { Button, Card, Icon, Input, Form, message, Alert } from 'antd';
import injectSheet from 'react-jss';
import withAuth from '../hoc/withAuth';
import { timeoutPromise } from '../../utils/utils';
import { Redirect } from 'react-router-dom';
import { tokens } from 'src/design-system';
import { mediaDown } from 'src/design-system/mixins/responsive';
import { fetchLogoAndId, loginRequest, postEmailAddress } from '../../../src/api/login';
import store from 'src/store/appStore';
import { setPasswordExpired } from 'src/actions/authentication';
const checkIcon = require('./../../assets/images/check.svg');

const FormItem = Form.Item;

const styles = (theme) => ({
  mainDiv: {
    minHeight: '100%',
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: tokens.spacing.md,
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: {
      left: tokens.spacing.lg,
      right: tokens.spacing.lg,
      bottom: tokens.spacing.lg,
    },
  },
  cardStyle: {
    width: '100%',
    maxWidth: 500,
  },
  inputDiv: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  loginButton: {
    height: 40,
    marginTop: tokens.spacing.sm,
    width: '100%',
  },
  errorMsg: {
    color: tokens.colors.error,
    fontSize: tokens.fontSize.body,
  },
  forgotPassword: {
    color: tokens.colors.primary,
    fontSize: tokens.fontSize.small,
    fontWeight: tokens.fontWeight.semibold,
    marginTop: tokens.spacing.xl,
    cursor: 'pointer',
  },
  ...mediaDown(tokens.breakpoint.sm, {
    mainDiv: {
      backgroundColor: tokens.colors.bgWhite,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardStyle: {
      width: '100%',
      maxWidth: 500,
      border: 'none',
    },
  }),
});

const defaultState = {
  emailValidator: {
    validateStatus: undefined,
    errorMsg: null,
  },
};

class Login extends React.PureComponent<any, any> {
  state = {
    redirectToReferrer: false,
    errorMsg: '',
    loginLoading: false,
    forgotPasswordContinueLoading: false,
    showForgotPasswordPage: false,
    showEmailSentPage: false,
    email: '',
    emailId: '',
    ...defaultState,
    logoURL: '',
    isMultipleSignInErrMsg: false,
  };

  async componentDidMount() {

    const response = await fetchLogoAndId();
    if (response.isSuccess) {
      localStorage.setItem('allowedSessions', response?.data?.allowedSessionsPerUser);
      window.localStorage.logoUrl = response.data.logoUrl;
      window.localStorage.setItem(
        'disable_sessionstack',
        response.data.disable_sessionstack ? 'true' : 'false'
      );
      window.localStorage.modules = JSON.stringify(
        response.data.modules ? response.data.modules : {}
      );
      window.localStorage.monitoringModules = JSON.stringify(
        response.data.monitoringModules ? response.data.monitoringModules : {}
      );
      window.localStorage.setItem('isTestOrg', response.data.isTestOrg);
      window.localStorage.setItem('isDevrevEnabled', response.data.isDevrevEnabled);
      this.setState({
        logoURL: response.data.logoUrl,
      });
    }
    await this.checkLogin();
  }

  setStorage = (
    organisationId,
    userId,
    accessToken,
    type,
    chaId,
    ffId,
    shippingLineId,
    email,
    username,
    role,
    phone,
  ) => {
    const storage = window.localStorage;
    storage.setItem('organisationId', organisationId);
    storage.setItem('userId', userId);
    storage.setItem('authToken', accessToken);
    storage.setItem('type', type);
    storage.setItem('chaId', chaId);
    storage.setItem('ffId', ffId);
    storage.setItem('shippingLineId', shippingLineId);
    storage.setItem('email', email);
    storage.setItem('username', username);
    storage.setItem('role', role);
    storage.setItem('phone', phone);
    storage.setItem('lastActionTime', String(new Date()));
  };

  removeError = () => {
    this.setState({ errorMsg: '', isMultipleSignInErrMsg: false });
  };

  showForgotPasswordModal = () => {
    this.setState({
      showForgotPasswordPage: true,
      showEmailSentPage: false,
    });
  };

  showLoginModal = () => {
    this.setState({
      showForgotPasswordPage: false,
      showEmailSentPage: false,
      emailValidator: {
        validateStatus: undefined,
        errorMsg: null,
        isMultipleSignInErrMsg: false,
      },
    });
  };

  handleEmailVerification = () => {
    const emailID = this.state.email;
    const getEmailValidator = {
      emailValidator: this.validateEmailInput(emailID),
    };

    this.setState({
      ...getEmailValidator,
    });
    if (getEmailValidator.emailValidator.validateStatus === 'success') {
      this.sendEmailAddress(emailID);
    }
  };

  sendEmailAddress = async (email) => {
    this.setState({ forgotPasswordContinueLoading: true });
    const response = await postEmailAddress({ email });

    if (response.isSuccess) {
      this.setState({
        showForgotPasswordPage: false,
        showEmailSentPage: true,
        emailId: email,
        emailValidator: {
          validateStatus: 'success',
          errorMsg: null,
          isMultipleSignInErrMsg: false,
        },
        email: '',
      });
    } else {
      this.setState({
        emailValidator: {
          validateStatus: 'error',
          errorMsg: 'This email ID is not associated with any account',
        },
      });
    }

    this.setState({ forgotPasswordContinueLoading: false });
  };

  validateEmailInput = (email) => {
    if (email) {
      return {
        validateStatus: 'success',
        errorMsg: null,
        isMultipleSignInErrMsg: false,
      };
    }
    return {
      validateStatus: 'error',
      errorMsg: 'Please input your email',
    };
  };

  handleEmailChange = (e) => {
    this.setState({
      email: e.target.value,
      emailValidator: {
        validateStatus: undefined,
        errorMsg: null,
        isMultipleSignInErrMsg: false,
      },
    });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      this.setState({ loginLoading: true });
      await timeoutPromise(500);

      if (err) {
        this.setState({ loginLoading: false });
      } else {
        this.handleLogin(values.userName, values.password);
      }
    });
  };

  handleLogin = async (usernameOrEmail, password) => {
    const isAlreadyLoggedIn = await this.checkLogin();
    if (!isAlreadyLoggedIn) {
      const response = await loginRequest({ usernameOrEmail, password });
      if (response.isSuccess) {
        // BE flag: when the user's password has expired, force them into the
        // change-password screen before completing the normal login flow.
        // We still call setStorage so the change-password API has a valid token
        // and `withAuth` recognises the user; navigation is blocked elsewhere by
        // the RouterOutlet guard (Task 6).
        if (response.data && response.data.passwordExpired === true) {
          this.setStorage(
            response.data.organisationId,
            response.data.userId,
            response.data.accessToken,
            response.data.type,
            response.data.cha_id,
            response.data.ff_id,
            response.data.shipping_line_id,
            response.data.email,
            response.data.username,
            response.data.role,
            response.data.phone,
          );
          this.setState({ loginLoading: false });
          // Redux thunk middleware isn't typed on `store.dispatch`; `as any` is the
          // project-wide workaround used at other store.dispatch sites.
          store.dispatch(setPasswordExpired() as any);
          window.location.assign('/account?forceChangePassword=1');
          return;
        }
        if (response.data && response.data.redirection_link) {
          window.location.replace(response.data.redirection_link);
        } else {
          this.setStorage(
            response.data.organisationId,
            response.data.userId,
            response.data.accessToken,
            response.data.type,
            response.data.cha_id,
            response.data.ff_id,
            response.data.shipping_line_id,
            response.data.email,
            response.data.username,
            response.data.role,
            response.data.phone,
          );
          this.setState({ redirectToReferrer: true });
        }
      } else {
        this.setState({
          loginLoading: false,
        });
        const errorMsg = response.errorMessage;
        const multipleSignInRegex = /multiple.*sign-in.*error/gim;

        if (errorMsg && multipleSignInRegex.test(errorMsg)) {
          this.setState({
            errorMsg: null,
            isMultipleSignInErrMsg: true,
          });
        } else {
          this.setState({ errorMsg, isMultipleSignInErrMsg: false });
        }
      }
    }
  };

  checkLogin = async () => {
    if (localStorage.getItem('authToken') && localStorage.getItem('organisationId')) {
      window.location.href = '/';
      return true;
    }
    return false;
  };

  renderMultipleSignInErrorMsg = () => {
    const sessions = localStorage.getItem('allowedSessions') || '1';
    return (
      <div>
        <div style={{ color: tokens.colors.textSecondary }}>
          More than {sessions} access requested from this account
        </div>
        <small style={{ color: tokens.colors.textTertiary }}>
          Looks like you are viewing Shipsy from more than {sessions} system(s). Please log out from
          any extra browser or system and reload the page.
        </small>
      </div>
    );
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const { from } = this.props.location.state || { from: { pathname: '/' } };
    const {
      redirectToReferrer,
      errorMsg,
      loginLoading,
      showForgotPasswordPage,
      forgotPasswordContinueLoading,
      logoURL,
      showEmailSentPage,
      emailId,
      isMultipleSignInErrMsg,
    } = this.state;
    const { classes } = this.props;
    if (redirectToReferrer) {
      return (
        <Redirect
          to={{
            ...from,
            state: { first_time_master_fetch: true },
          }}
        />
      );
    }
    if (showForgotPasswordPage) {
      return (
        <div className={classes.mainDiv}>
          <Card className={classes.cardStyle}>
            <Form onSubmit={this.handleSubmit} className="login-form">
              <div className={classes.cardDiv}>
                <h2
                  style={{
                    marginTop: tokens.spacing.lg,
                    color: tokens.colors.text,
                    fontSize: tokens.fontSize.body,
                    textAlign: 'center',
                  }}
                >
                  Enter your work email ID to receive an email to recover your password.
                </h2>
                <div className={classes.inputDiv} style={{ margin: '30px 0 60px 0' }}>
                  <FormItem
                    validateStatus={this.state.emailValidator.validateStatus}
                    help={this.state.emailValidator.errorMsg}
                  >
                    <Input
                      size="large"
                      placeholder="Email address"
                      onChange={this.handleEmailChange}
                      value={this.state.email}
                    />
                  </FormItem>
                </div>
                <Button
                  type="primary"
                  htmlType="submit"
                  className={classes.loginButton}
                  loading={forgotPasswordContinueLoading}
                  disabled={forgotPasswordContinueLoading}
                  onClick={this.handleEmailVerification}
                >
                  Continue
                </Button>
                <div className={classes.forgotPassword} onClick={this.showLoginModal}>
                  Login
                </div>
              </div>
            </Form>
          </Card>
        </div>
      );
    }

    if (showEmailSentPage) {
      return (
        <div className={classes.mainDiv}>
          <Card className={classes.cardStyle}>
            <div className={classes.cardDiv}>
              <img style={{ height: '42px', marginTop: '20px' }} src={checkIcon} />
              <h3 style={{ color: tokens.colors.textSecondary, fontWeight: tokens.fontWeight.semibold, marginTop: tokens.spacing.lg }}>Email Sent</h3>
              <h2
                style={{
                  color: tokens.colors.text,
                  fontSize: tokens.fontSize.body,
                  textAlign: 'center',
                }}
              >
                A recovery link has been sent to <b>{emailId}</b> . Please check your email.
              </h2>
              <div className={classes.forgotPassword} onClick={this.showForgotPasswordModal}>
                Resent Recovery Link
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return (
      <div className={classes.mainDiv}>
        {isMultipleSignInErrMsg ? (
          <Alert
            type="error"
            message={this.renderMultipleSignInErrorMsg()}
            style={{ width: '100%', maxWidth: 500 }}
          ></Alert>
        ) : null}
        <Card className={classes.cardStyle}>
          <Form onSubmit={this.handleSubmit} className="login-form">
            <div className={classes.cardDiv}>
              <img style={{ height: 'auto', maxWidth: '100%', maxHeight: '100px' }} src={logoURL} />
              <h2 style={{ marginTop: tokens.spacing.lg, fontWeight: tokens.fontWeight.semibold, color: tokens.colors.text }}>
                Login to your account
              </h2>

              <div className={classes.inputDiv}>
                <span
                  style={{
                    fontSize: tokens.fontSize.small,
                    color: tokens.colors.textTertiary,
                    marginBottom: tokens.spacing.xs,
                    marginTop: '20px',
                  }}
                >
                  Username/Email
                </span>
                <FormItem>
                  {getFieldDecorator('userName', {
                    rules: [{ required: true, message: 'Please input your username!' }],
                  })(
                    <Input
                      size="large"
                      placeholder="Enter Username/Email"
                      onChange={this.removeError}
                    />
                  )}
                </FormItem>
              </div>

              <div className={classes.inputDiv}>
                <span
                  style={{
                    fontSize: tokens.fontSize.small,
                    color: tokens.colors.textTertiary,
                    marginBottom: tokens.spacing.xs,
                    marginTop: tokens.spacing.sm,
                  }}
                >
                  Password
                </span>
                <FormItem>
                  {getFieldDecorator('password', {
                    rules: [{ required: true, message: 'Please input your Password!' }],
                  })(
                    <Input
                      type="password"
                      size="large"
                      placeholder="Enter Password"
                      onChange={this.removeError}
                    />
                  )}
                </FormItem>
              </div>

              {errorMsg ? (
                <div className={classes.errorMsg}>
                  <span>{errorMsg}</span>
                </div>
              ) : null}

              <Button
                type="primary"
                className={classes.loginButton}
                htmlType="submit"
                loading={loginLoading}
                disabled={loginLoading}
              >
                Login
              </Button>

              <div className={classes.forgotPassword} onClick={this.showForgotPasswordModal}>
                Forgot Password
              </div>
            </div>
          </Form>
        </Card>
      </div>
    );
  }
}

export default withAuth(Form.create()(injectSheet(styles)(Login)));
